import Popover from "@mui/material/Popover";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import React, {
  RefObject,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Trans } from "react-i18next";

import Alert from "assets/icons/Alert.svg?react";

import { useMenu } from "components/ActionMenu/useMenu";
import ContextMenu from "components/ContextMenu/ContextMenu";
import ContextMenuItemsGroup from "components/ContextMenu/ContextMenuItemsGroup";
import { CloseIcon, DropdownArrowDown } from "components/Icons";
import { InputDisabledContext } from "components/InputState/InputDisabledContext";
import { Search } from "components/Search";
import Tooltip from "components/Tooltip/Tooltip";

import styles from "./styles.module.scss";

export const MIN_WIDTH = 240;

type SearchOptions = {
  searchPlaceholder: string;
  noResultsText?: string;
  searchKey?: string;
  searchKeys?: string[];
  hideSearch?: boolean;
};

export const WithSearch = (props: {
  items?: React.ReactElement[];
  searchOptions?: SearchOptions;
  children: React.ReactElement;
  testId?: string;
}) => {
  const { children, items = [], searchOptions, testId } = props;
  const [search, setSearch] = useState("");

  if (!searchOptions || searchOptions.hideSearch) {
    return <div>{React.cloneElement(children, { items })}</div>;
  }

  const withDividers = items.filter(item => {
    if ((item?.type as any)?.role === "Divider") {
      return true;
    }
    if (item.props.skipFilter) {
      return true;
    }
    if (searchOptions.searchKeys) {
      for (const key of searchOptions.searchKeys) {
        const match = item.props[key]
          .toLowerCase()
          .includes(search.toLowerCase());
        if (match) {
          return match;
        }
      }
    }
    const key = searchOptions.searchKey || "value";
    return item.props[key].toLowerCase().includes(search.toLowerCase());
  });

  const withoutDividers = withDividers.filter(item => {
    return (item?.type as any)?.role !== "Divider";
  });

  const withoutSystemItems = withoutDividers.filter(
    item => !item.props.skipFilter
  );

  const { noResultsText, searchPlaceholder } = searchOptions;

  const defaultSearchString = (
    <div className={styles.noResults}>
      <span className={styles.noResults__message}>
        <Trans
          i18nKey={
            search ? "select.no_items_found_for" : "select.no_items_found"
          }
          values={{ search }}
          components={{ bold: <strong /> }}
        />
      </span>
    </div>
  );

  const renderItems = () => {
    const items = search ? withoutSystemItems : withoutDividers;

    return items.length ? (
      React.cloneElement(children, {
        items: withDividers,
        withoutSystemItems,
      })
    ) : (
      <div className={styles.notFound}>
        {noResultsText ? (
          <div className={styles.noResults}>
            <span className={styles.noResults__message}>{noResultsText}</span>
          </div>
        ) : (
          defaultSearchString
        )}
      </div>
    );
  };

  return (
    <div data-testid={testId}>
      <Search
        onChange={setSearch}
        value={search}
        autoFocus={true}
        size="small"
        placeholder={searchPlaceholder}
      />
      <div className={styles.selectorItems}>{renderItems()}</div>
    </div>
  );
};

export const WithSearchVirtualized = (props: {
  items: unknown[];
  // eslint-disable-next-line react/no-unused-prop-types
  prefixItems?: React.ReactElement[];
  renderItem: (item: any) => React.ReactElement;
  searchOptions?: SearchOptions;
  children: React.ReactElement;
  testId?: string;
}) => {
  const { testId, children, items, renderItem, searchOptions } = props;
  const [search, setSearch] = useState("");
  const parentRef = useRef<HTMLDivElement | null>(null);

  const foundItems = searchOptions
    ? items.filter(item => {
        const key = searchOptions.searchKey || "value";
        return (item as any)[key].toLowerCase().includes(search.toLowerCase());
      })
    : items;

  const virtualizer = useVirtualizer({
    count: foundItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 32,
  });

  const virtualItems = virtualizer.getVirtualItems();

  const renderedItems = virtualItems.map(virtualRow => {
    const index = virtualRow.index;
    const item = foundItems[index];
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: `${virtualRow.size}px`,
          transform: `translateY(${virtualRow.start}px)`,
        }}
        key={index}
      >
        {renderItem(item)}
      </div>
    );
  });

  const height = virtualizer.getTotalSize();

  if (!searchOptions) {
    return <div>{React.cloneElement(children, { items: renderedItems })}</div>;
  }

  const { noResultsText, searchPlaceholder } = searchOptions;

  const defaultSearchString = (
    <div className={styles.noResults}>
      <span className={styles.noResults__message}>
        <Trans
          i18nKey={
            search ? "select.no_items_found_for" : "select.no_items_found"
          }
          values={{ search }}
          components={{ bold: <strong /> }}
        />
      </span>
    </div>
  );

  return (
    <div data-testid={testId}>
      <Search
        onChange={setSearch}
        value={search}
        autoFocus={true}
        size="small"
        placeholder={searchPlaceholder}
      />
      <div className={styles.selectorItems}>
        {foundItems.length ? (
          React.cloneElement(children, {
            items: renderedItems,
            withoutSystemItems: foundItems,
            height,
            parentRef,
          })
        ) : (
          <div className={styles.notFound}>
            {noResultsText ? (
              <div className={styles.noResults}>
                <span className={styles.noResults__message}>
                  {noResultsText}
                </span>
              </div>
            ) : (
              defaultSearchString
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Select = (props: {
  handleSelect: (item: unknown, bulkItems: string[]) => void;
  items?: React.ReactElement[];
}) => {
  const { items = [], handleSelect } = props;

  const bulkItems = items
    .filter(item => item.props.bulkItem)
    .map(item => item.props.value);

  const menuElements = items.reduce<React.ReactElement[]>(
    (acc, item, index) => {
      if (
        (item?.type as any)?.role === "Divider" &&
        index === items.length - 1
      ) {
        return acc;
      }

      if ((item?.type as any)?.role === "Divider") {
        return [...acc, item];
      }

      const element = React.cloneElement(item, {
        onClick: handleSelect(item.props, bulkItems),
        handleSelect,
      });

      return [...acc, element];
    },
    []
  );

  return (
    <ContextMenu>
      <ContextMenuItemsGroup
        isGroup
        maxHeight={400}
      >
        {menuElements}
      </ContextMenuItemsGroup>
    </ContextMenu>
  );
};

type Props = {
  className?: string;
  wrapperClassName?: string;
  multiple?: boolean;
  disabled?: boolean;
  allowUncheck?: boolean;
  searchOptions?: SearchOptions;
  initialSelected?: string[];
  controlledValue?: string[];
  onSelect?: (items: string[]) => void;
  onClose?: () => void;
  renderValue?: (selectedItems: string[]) => React.ReactNode;
  renderMenu?: (handleClose: () => void) => React.ReactElement;
  children?: React.ReactElement[];
  error?: string;
  popoverProps?: any;
  testId?: string;
  noBorder?: boolean;
  showFilterApplied?: boolean;
  alertTooltip?: string;
  valueClassName?: string;
  popoverRef?: RefObject<HTMLDivElement>;
  prefix?: React.ReactNode;
};

export const OutlinedSelect = React.forwardRef((props: Props, ref) => {
  const {
    className,
    multiple,
    allowUncheck = false,
    searchOptions,
    initialSelected = [],
    onSelect,
    renderValue,
    error,
    popoverProps,
    renderMenu,
    onClose,
    testId,
    wrapperClassName,
    controlledValue,
    noBorder,
    alertTooltip,
    popoverRef,
    prefix,
    showFilterApplied = false,
    valueClassName,
  } = props;
  const { menuElement, openMenu, closeMenu } = useMenu();
  const [focus, setFocus] = useState(false);
  const isDisabled = useContext(InputDisabledContext);
  const disabled = props.disabled || isDisabled;

  useImperativeHandle(ref, () => ({
    closeMenu,
  }));

  const selectRef = useRef<HTMLDivElement | null>(null);
  const menuMinWidth = useRef<number>(0);
  const [selectedItems, setSelectedItem] = useState<string[]>(initialSelected);

  useEffect(() => {
    if (controlledValue !== undefined) {
      setSelectedItem(controlledValue || []);
    }
  }, [controlledValue]);

  const setInitialWidth = () => {
    const { width = 0 } = selectRef.current?.getBoundingClientRect() || {};
    menuMinWidth.current = width;
  };

  const handleOpen = (event: React.MouseEvent) => {
    if (disabled) {
      return;
    }
    setInitialWidth();
    openMenu(event);
  };

  const handleKeyDown: React.KeyboardEventHandler = event => {
    if (disabled) {
      return;
    }
    if (event.key === "Enter" || event.key === "ArrowDown") {
      setInitialWidth();
      openMenu(event);
    }
  };

  const handleSelect = (item: unknown, bulkItems: string[]) => () => {
    const { value, disableClose, bulkItem } = item as {
      value: string;
      disableClose?: boolean;
      bulkItem?: boolean;
    };

    const getItems = () => {
      if (bulkItem) {
        return selectedItems.includes(value) ? [] : [value];
      }
      if (multiple) {
        const selected = selectedItems.includes(value)
          ? selectedItems.filter(i => i !== value)
          : [...selectedItems, value];
        return selected.filter(item => !bulkItems.includes(item));
      }
      if (allowUncheck) {
        return selectedItems.includes(value) ? [] : [value];
      }

      return [value];
    };

    const items = getItems();

    if (!controlledValue) {
      setSelectedItem(items);
    }
    onSelect && onSelect(items);
    !disableClose && !multiple && closeMenu();
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!controlledValue) {
      setSelectedItem([]);
    }
    onSelect && onSelect([]);
  };

  const value = renderValue
    ? renderValue(selectedItems)
    : selectedItems.join(", ");

  const handleClose = () => {
    closeMenu();
    onClose && onClose();
  };

  const handleFocus = () => setFocus(true);
  const handleBlur = () => setFocus(false);

  return (
    <div
      className={classNames(styles.wrapper, wrapperClassName, {
        [styles.error]: error,
        [styles.focus]: focus,
        [styles.active]: !!menuElement,
        [styles.noBorder]: noBorder,
      })}
      data-testid={testId}
    >
      <div
        className={classNames(styles.select, className, {
          [styles.disabled]: disabled,
          [styles.noBorder]: noBorder,
          [styles.filterApplied]: showFilterApplied && selectedItems.length > 0,
        })}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        ref={selectRef}
        tabIndex={0}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-testid={`${testId}-inner-select`}
      >
        {prefix && <div className={styles.prefix}>{prefix}</div>}
        <div
          data-testid={`${testId}-value`}
          className={classNames(styles.value, valueClassName)}
        >
          {value}
        </div>
        <div className={styles.right}>
          {alertTooltip && (
            <Tooltip title={alertTooltip}>
              <div className={styles.alertTooltip}>
                <Alert />
              </div>
            </Tooltip>
          )}
          {!!selectedItems.length && multiple && (
            <CloseIcon
              className={styles.clear}
              onClick={handleClearSelection}
            />
          )}
          <DropdownArrowDown className={styles.dropdownIcon} />
        </div>
      </div>
      {!!error && <span className={styles.error__label}>{error}</span>}
      {!!menuElement && (
        <Popover
          open
          anchorEl={menuElement}
          classes={{ paper: styles.paper }}
          onClose={handleClose}
          marginThreshold={8}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          slotProps={{
            paper: {
              style: {
                minWidth: menuMinWidth.current,
                width: Math.max(menuMinWidth.current, MIN_WIDTH),
                marginTop: 2,
              },
            },
          }}
          {...popoverProps}
        >
          <div
            ref={popoverRef}
            data-testid={`select-popover-${testId}`}
          >
            <WithSearch
              items={props.children}
              searchOptions={searchOptions}
            >
              {renderMenu ? (
                renderMenu(handleClose)
              ) : (
                <Select handleSelect={handleSelect} />
              )}
            </WithSearch>
          </div>
        </Popover>
      )}
    </div>
  );
});
